import Wordmark from '@/components/layout/Wordmark'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

export default function TermsPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d0a1f] to-[#12103a] text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/50 hover:text-white/80 transition mb-8 py-2 -ml-2 pl-2 pr-3 rounded-lg"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <Wordmark size="lg" variant="light" className="justify-center mb-4" />

        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2">Terms of Service</h1>
        <p className="text-white/50 text-center mb-12">
          Effective Date: May 1, 2026
        </p>

        <div className="space-y-10 text-white/80 leading-relaxed">
          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">1.</span>Acceptance of Terms
            </h2>
            <p>
              By accessing or using the LegalLady AI platform ("Service"), available at legallady.ai,
              app.legallady.ai, our iOS and Android mobile applications, and any future applications or
              services operated by LegalLady AI, a California company, you agree to be bound by these
              Terms of Service ("Terms"). If you do not agree, do not use the Service. These Terms
              apply to all users across all platforms, including law firm administrators, team members,
              and any individuals interacting with AI-powered chat or voice agents deployed through
              the platform.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">2.</span>Description of Service
            </h2>
            <p>
              LegalLady AI is a B2B SaaS platform accessible via web (legallady.ai, app.legallady.ai),
              iOS, and Android that provides law firms with AI-powered legal intake tools, including:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>An embeddable chat widget for law firm websites that engages visitors 24/7</li>
              <li>AI voice calling agents (inbound receptionist and outbound intake specialist)</li>
              <li>Phone number provisioning for AI voice agents</li>
              <li>Lead management, call logs, chat history, and booking tools</li>
              <li>Team collaboration with internal messaging, role-based access, and organization management</li>
              <li>Analytics, reporting, and CSV data export</li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">3.</span>Important Disclaimer — Not a Law Firm
            </h2>
            <p className="font-semibold text-white">
              LegalLady AI is a technology platform. We are NOT a law firm, do NOT provide legal
              advice, and do NOT establish attorney-client relationships.
            </p>
            <p className="mt-2">
              The AI agents deployed through our platform are designed to assist with intake and
              information gathering only. Any information provided by AI chat or voice agents is
              general in nature and should not be relied upon as legal counsel. Law firms using the
              platform are solely responsible for all legal advice given to their clients.
            </p>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">4.</span>Account Responsibilities
            </h2>
            <p>
              You are responsible for maintaining the security of your account credentials and for
              all activity that occurs under your account. You must provide accurate and complete
              information during registration and keep it current. You agree to notify us immediately
              of any unauthorized use of your account.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">5.</span>Subscription and Billing
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Subscription Activation:</strong> Standard signups
                begin a paid subscription immediately upon checkout. A valid payment method is
                required and is charged for the selected plan starting on the date of subscription.
                No trial is included in the default signup flow.
              </li>
              <li>
                <strong className="text-white">Invitation-Based Trial:</strong> Eligible new
                accounts may be granted a 14-day free trial via an invitation or access code. When
                granted, the trial provides full access to a designated subscription tier. A valid
                payment method may be required at signup. If you do not cancel before the end of
                the trial, you authorize us to automatically charge your payment method for the
                selected plan. You may cancel a trial at any time before it ends through your
                account's Billing page; if cancelled during the trial, your payment method will not
                be charged.
              </li>
              <li>
                <strong className="text-white">Auto-Renewal:</strong> Paid subscriptions
                automatically renew at the end of each billing cycle unless cancelled before the
                renewal date.
              </li>
              <li>
                <strong className="text-white">Cancellation:</strong> You may cancel your
                subscription at any time through your account's Billing page. Cancellation takes
                effect at the end of the current billing period. No refunds are provided for
                partial periods.
              </li>
              <li>
                <strong className="text-white">Price Changes:</strong> We may adjust pricing with
                at least 30 days' notice before your next billing cycle.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">6.</span>Acceptable Use
            </h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Use AI agents for any unlawful, fraudulent, or deceptive purpose</li>
              <li>Abuse, manipulate, or attempt to exploit AI chat or voice agents</li>
              <li>Transmit malware, spam, or harmful content through the platform</li>
              <li>Impersonate a licensed attorney through the AI agents</li>
              <li>Use the Service in any way that violates applicable federal, state, or local laws</li>
              <li>Attempt to reverse-engineer, decompile, or extract the source code of the Service</li>
            </ul>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">7.</span>Data and Content Ownership
            </h2>
            <p>
              You retain ownership of all data you submit to the Service, including organization
              information, lead data, chat transcripts, call recordings, and any other content
              ("Customer Data"). By using the Service, you grant LegalLady AI a limited,
              non-exclusive license to process, store, and transmit your Customer Data solely for the
              purpose of providing and improving the Service.
            </p>
            <p className="mt-2">
              LegalLady AI retains ownership of the platform, all software, AI models, algorithms,
              and any aggregated or anonymized data derived from usage of the Service.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">8.</span>AI-Generated Content Disclaimer
            </h2>
            <p>
              Responses generated by AI chat and voice agents may contain inaccuracies, incomplete
              information, or errors. AI-generated content is not a substitute for professional legal
              advice. Law firms deploying LegalLady AI agents are solely responsible for reviewing
              all AI-generated responses and ensuring accuracy before relying on them for any legal
              matter.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">9.</span>Disclaimer of Warranties
            </h2>
            <p>
              LegalLady AI provides the Service on an "AS IS" and "AS AVAILABLE" basis. To the
              maximum extent permitted by applicable law, we expressly disclaim all warranties of any
              kind, whether express, implied, or statutory, including but not limited to implied
              warranties of merchantability, fitness for a particular purpose, title, and
              non-infringement.
            </p>
            <p className="mt-2">
              We do not warrant that: (a) the Service will meet your requirements or expectations;
              (b) the Service will be uninterrupted, timely, secure, or error-free; (c) AI-generated
              responses will be accurate, complete, or appropriate for any specific legal matter;
              (d) any calls, messages, or communications facilitated through the Service will be
              delivered successfully or without error; or (e) any defects in the Service will be
              corrected.
            </p>
            <p className="mt-2">
              You acknowledge that AI chat agents, voice agents, and automated outbound calls may
              produce inaccurate responses, miss calls, fail to connect, schedule incorrect
              appointments, or otherwise malfunction. LegalLady AI is not liable for any damages
              resulting from such occurrences.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">10.</span>Service Availability
            </h2>
            <p>
              LegalLady AI does not guarantee any specific level of uptime, availability, or
              performance. The Service may be temporarily unavailable due to scheduled maintenance,
              unscheduled outages, third-party service provider failures, or circumstances beyond our
              control. We will make commercially reasonable efforts to provide reliable service but
              make no guarantees regarding availability.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">11.</span>Force Majeure
            </h2>
            <p>
              LegalLady AI shall not be liable for any delay or failure in performance resulting from
              causes beyond our reasonable control, including but not limited to: acts of God, natural
              disasters, war, terrorism, pandemics, government actions, power or internet outages,
              failures of third-party service providers (including but not limited to telephony,
              hosting, payment, and telecommunications providers), cyberattacks, or any other force majeure
              event.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">12.</span>Phone and Voice Services
            </h2>
            <p>
              LegalLady AI provides AI-powered voice agents for inbound and outbound calls. By using
              these features:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>You acknowledge that calls handled by AI voice agents may be recorded for quality assurance, training, and record-keeping purposes</li>
              <li>You are responsible for complying with all applicable call recording laws, including obtaining consent from callers where required by law (e.g., California's two-party consent law)</li>
              <li>Phone numbers provisioned through the Service remain the property of LegalLady AI and its telephony providers</li>
            </ul>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">13.</span>Outbound Calling Compliance (TCPA)
            </h2>
            <p>
              LegalLady AI's outbound calling features are designed to contact only individuals who
              have provided prior express consent through a lead intake form, chat widget, or
              equivalent opt-in mechanism. By using the outbound calling features, you represent and
              warrant that:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>You will only initiate outbound calls to individuals who have provided valid prior express consent to be contacted</li>
              <li>You will comply with all applicable laws and regulations governing telephone communications, including the Telephone Consumer Protection Act (TCPA), state telemarketing laws, and Do Not Call (DNC) registry requirements</li>
              <li>You will maintain records of consent for all outbound call recipients</li>
              <li>You will not use the platform to place calls to numbers on the National Do Not Call Registry without valid prior express consent</li>
            </ul>
            <p className="mt-2">
              LegalLady AI provides the technology platform only. Law firms are solely responsible for
              ensuring valid consent exists before initiating any outbound communication. LegalLady AI
              shall not be liable for any TCPA violations, fines, or damages arising from outbound
              calls placed by or on behalf of law firm subscribers.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">14.</span>AI Disclosure Requirement
            </h2>
            <p>
              Law firms deploying LegalLady AI chat or voice agents must clearly disclose to consumers
              that they are interacting with an artificial intelligence system, not a human. This
              disclosure should be made at the beginning of each interaction through the agent's
              greeting or introductory message. Failure to provide adequate AI disclosure may violate
              applicable laws and regulations. LegalLady AI is not responsible for law firms' failure
              to properly disclose AI usage to their clients or prospective clients.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">15.</span>Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, LegalLady AI and its officers, employees,
              and agents shall not be liable for any indirect, incidental, special, consequential, or
              punitive damages arising from your use of the Service, including but not limited to
              loss of revenue, data, or business opportunities. Our total liability for any claim
              shall not exceed the amount you paid for the Service in the 12 months preceding the
              claim.
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">16.</span>Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless LegalLady AI and its affiliates from
              any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising
              from your use of the Service, your violation of these Terms, or your violation of any
              third-party rights.
            </p>
          </section>

          {/* 17 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">17.</span>Termination
            </h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without
              cause, upon reasonable notice. Upon termination, your right to use the Service ceases
              immediately. You may request export of your Customer Data within 30 days of
              termination, after which we may delete it.
            </p>
          </section>

          {/* 18 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">18.</span>Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of the State of California, without regard to
              conflict of law principles. Any disputes arising under these Terms shall be resolved
              exclusively in the state or federal courts located in California. You consent to the
              personal jurisdiction of such courts.
            </p>
          </section>

          {/* 19 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">19.</span>Dispute Resolution and Arbitration
            </h2>
            <p>
              Any dispute, claim, or controversy arising out of or relating to these Terms or the
              Service shall be resolved through binding arbitration administered by the American
              Arbitration Association (AAA) under its Commercial Arbitration Rules. Arbitration shall
              take place in California, and the arbitrator's decision shall be final and binding.
            </p>
            <p className="mt-2">
              You agree that any arbitration shall be conducted on an individual basis and not as a
              class, consolidated, or representative action. You waive any right to participate in a
              class action lawsuit or class-wide arbitration against LegalLady AI.
            </p>
            <p className="mt-2">
              Notwithstanding the above, either party may seek injunctive or equitable relief in a
              court of competent jurisdiction to prevent the actual or threatened infringement of
              intellectual property rights.
            </p>
          </section>

          {/* 20 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">20.</span>CCPA Compliance
            </h2>
            <p>
              LegalLady AI complies with the California Consumer Privacy Act (CCPA). For details on
              how we collect, use, and protect personal information, and how California residents can
              exercise their rights, please see our{' '}
              <a href="/privacy" className="text-indigo-400 hover:text-indigo-300 underline">
                Privacy Policy
              </a>
              .
            </p>
          </section>

          {/* 21 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">21.</span>Modification of Terms
            </h2>
            <p>
              We may update these Terms from time to time. When we make material changes, we will
              notify you via email or through the platform at least 15 days before the changes take
              effect. Continued use of the Service after changes become effective constitutes
              acceptance of the updated Terms.
            </p>
          </section>

          {/* 22 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-2">
              <span className="text-white/50 mr-2">22.</span>Contact Us
            </h2>
            <p>
              If you have questions about these Terms, contact us at:
            </p>
            <div className="mt-2">
              <p>LegalLady AI</p>
              <p>
                Email:{' '}
                <a href="mailto:support@legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                  support@legallady.ai
                </a>
              </p>
              <p>
                Website:{' '}
                <a href="https://legallady.ai" className="text-indigo-400 hover:text-indigo-300 underline">
                  legallady.ai
                </a>
              </p>
            </div>
          </section>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 text-center text-white/40 text-sm">
          &copy; {new Date().getFullYear()} LegalLady AI. All rights reserved.
        </div>
      </div>
    </div>
  )
}
